import { useEffect, useRef } from 'react';

import { AudioPlayer, AudioSource, createAudioPlayer, setAudioModeAsync } from 'expo-audio';
import { EventSubscription } from 'expo-notifications/build/Notifications.types';
import { noop } from 'lodash-es';

import { region } from 'utils/environment';
import { Region } from 'utils/environment/types';
import logger from 'utils/logger';

// Defined locally because expo-audio doesn't export this constant on web
const PLAYBACK_STATUS_UPDATE = 'playbackStatusUpdate';

export const takeoverAudioMuted = { current: true };

setAudioModeAsync({
  playsInSilentMode: true,
  interruptionMode: 'mixWithOthers',
  interruptionModeAndroid: 'duckOthers',
  allowsRecording: false,
  shouldPlayInBackground: false,
  shouldRouteThroughEarpiece: false,
}).catch(noop);

const players = new Map<AudioPlayer, { priority: number; subscription: EventSubscription }>();

const removePlayer = (player: AudioPlayer | null) => {
  try {
    // just in case there are issues here don't bomb the app.
    if (!player || !players.has(player)) {
      return;
    }
    const subscription = players.get(player)?.subscription;
    players.delete(player);
    player.pause();
    player.release();
    subscription?.remove();
  } catch (error) {
    logger.error({
      message: `Error removing audio player`,
      error,
    });
  }
};

/** will play audio once if its defined and allowed */
export const maybePlayAudioOnce = (
  source: AudioSource | string | number | null = null,
  delay = 1000,
  priority = 0
) => {
  let currentPlayer: AudioPlayer | null = null;
  try {
    if (region === Region.CA) {
      return; // audio is disabled for CA for now
    }

    if (takeoverAudioMuted.current) {
      return;
    }

    if (!source) {
      return;
    }

    currentPlayer = createAudioPlayer(source, { updateInterval: 100 });
    const subscription = currentPlayer.addListener(PLAYBACK_STATUS_UPDATE, status => {
      if (status.didJustFinish) {
        removePlayer(currentPlayer);
      }
    });
    players.set(currentPlayer, { priority, subscription });
    setTimeout(() => {
      if (currentPlayer && players.get(currentPlayer)) {
        let wasLowerPriorityCancelled = false;
        // we only want on audio playing at once, kill all other players if they have started playing (if not they are likely queued up)
        players.forEach(({ priority: otherPriority }, otherPlayer) => {
          if (otherPriority > priority) {
            wasLowerPriorityCancelled = true;
            return;
          }
          if (currentPlayer !== otherPlayer && otherPlayer.currentTime > 0) {
            removePlayer(otherPlayer);
          }
        });
        if (wasLowerPriorityCancelled) {
          removePlayer(currentPlayer);
        } else {
          currentPlayer.play();
        }
      }
    }, delay);

    return () => removePlayer(currentPlayer);
  } catch (_error) {
    // web may throw if user didn't interact with dom yet. eat this error.
    removePlayer(currentPlayer);
    return noop;
  }
};

const playedAudioOnceSet = new Set<AudioSource | string | number>();

/** play audio once on mount if it is defined */
export const useMaybePlayAudioOnceOnMount = (
  source: AudioSource | string | number | null = null,
  {
    shouldPlay = true,
    delay = 1000,
    priority = 0,
    globalPlayOnce = false,
  }: { shouldPlay?: boolean; delay?: number; priority?: number; globalPlayOnce?: boolean } = {}
) => {
  const didPlayRef = useRef(false);

  useEffect(() => {
    if (source && !didPlayRef.current && shouldPlay && !playedAudioOnceSet.has(source)) {
      const removePlayer = maybePlayAudioOnce(source, delay, priority);
      didPlayRef.current = true;
      if (globalPlayOnce) {
        playedAudioOnceSet.add(source);
      }
      return () => removePlayer?.(); // avoids double playing if component mounting is funky
    }
  }, [source, shouldPlay, delay, priority, globalPlayOnce]);
};
